"use client";
// "use client" hoja interactiva: copy localizada (useT) + reveals con IntersectionObserver — sin estado propio.
// 2026-08-23 performance budget — LCP <2.5s, INP <200ms, CLS <0.1, 42% pass only;
// 2026-08-23 TrustSignals — social proof, no fake numbers, audit-claims verified;

import { ProviderLogo } from "@/components/ai/ProviderLogos";
import type { AiProvider } from "@/lib/ai-engine/providers";
import type { TranslationKey } from "@/lib/i18n/locales";
import { useLocale, useT } from "@/lib/use-i18n";
import { Reveal } from "./Reveal";

const AI_PROVIDERS: Array<{ provider: AiProvider; name: string }> = [
  { provider: "openai", name: "OpenAI" },
  { provider: "gemini", name: "Gemini" },
  { provider: "anthropic", name: "Anthropic" },
  { provider: "deepseek", name: "DeepSeek" },
];

const STATS: Array<{ valueKey: TranslationKey; labelKey: TranslationKey }> = [
  {
    valueKey: "socialStatLanguagesValue",
    labelKey: "socialStatLanguagesLabel",
  },
  { valueKey: "socialStatPrivacyValue", labelKey: "socialStatPrivacyLabel" },
  { valueKey: "socialStatSignupValue", labelKey: "socialStatSignupLabel" },
  { valueKey: "socialStatSpeedValue", labelKey: "socialStatSpeedLabel" },
];

export function TrustSignals() {
  const t = useT();
  const locale = useLocale();
  // 2026-09-25 RUN1 FASE3-FIX (SEED=2026-09-25-c6c873bf; research 2026
  // accessibility.build 2026-08 spaceless CJK https://accessibility.build/guides/accessible-typography-wcag:
  // uppercase + tracking 0.08em + truncate rompe JA/ZH/KO (sin mayúsculas ni
  // espacios — el tracking abre huecos y el truncate corta sin palabra) — en
  // CJK se quita uppercase/tracking, title intacto para tooltip/SR).
  const isCjk = locale === "ja" || locale === "zh" || locale === "ko";

  return (
    <section
      aria-label={t("socialChatKicker")}
      className="no-x-overflow border-y border-border/50 bg-[var(--card)]/40 py-10 sm:py-14"
      data-testid="social-chat"
    >
      <div className="mx-auto max-w-container-wide space-y-8 gutter-fluid">
        <div className="space-y-4 text-center">
          {/* 2026-09-24 FASE 7 tipografía (research 2026 UI type scale:
              tracking cap 0.08em SSOT — solo tracking, superficie de
              transparencia de proveedores intacta). */}
          <h2
            className={
              isCjk
                ? "break-words text-balance text-xs font-semibold text-muted-foreground sm:truncate"
                : "break-words text-balance text-xs font-semibold uppercase tracking-[0.08em] text-muted-foreground sm:truncate"
            }
            title={t("socialProvidersTitle")}
          >
            {t("socialProvidersTitle")}
          </h2>
          {/* 2026-09-23 TAREA F responsive-parity (móvil ← PC, sin cambiar
              PC): el marquee es el comportamiento de PC — se renderiza en TODOS
              los viewports (antes `hidden sm:block` en 360px con fila estática
              aparte). Solo se adapta tamaño (size-8 en móvil, size-10 en sm+);
              mismo flujo, mismos 4 proveedores, mismos CTAs. Tokens Lovable
              intactos. */}
          {/* Seamless infinite marquee con máscara de desvanecimiento suave en bordes */}
          <div className="vortixy-provider-marquee relative block min-h-[32px] sm:min-h-[40px] [mask-image:linear-gradient(to_right,transparent,black_12%,black_88%,transparent)]">
            <div className="vortixy-provider-marquee-track min-h-[32px] sm:min-h-[40px]">
              {/* Seamless infinite: render enough groups to always fill viewport (2026 fix). 4 providers × 6 copies = 24 logos → ~3.7× viewport at 1920px, no visible repetition/cut */}
              {Array.from({ length: 6 }).map((_, groupIndex) => (
                <div
                  key={groupIndex}
                  className="vortixy-provider-marquee-group"
                  aria-hidden={groupIndex !== 0 || undefined}
                >
                  {AI_PROVIDERS.map((provider) => (
                    <div
                      key={`${provider.name}-${groupIndex}`}
                      className="vortixy-provider-logo transition-transform duration-200 hover:scale-105 motion-reduce:transition-none motion-reduce:transform-none"
                    >
                      <ProviderLogo
                        provider={provider.provider}
                        className="size-8 sm:size-10"
                      />
                      {/* Icon-only chips (2026-08-26): official brand mark without wordmark text; name kept for screen readers */}
                      <span className="sr-only">{provider.name}</span>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          </div>
        </div>

        <Reveal className="trust-rail">
          {/* 2026-09-06 a11y: sin role=group sin nombre ni aria-live (contenido
              estático — el live anunciaba en cada render sin cambios) */}
          {STATS.map((stat) => (
            <div key={stat.labelKey} className="trust-cell min-w-0">
              <span className="trust-value font-bold tracking-tight tabular-nums break-words max-w-full">
                {t(stat.valueKey)}
              </span>
              {/* 2026-10-03 RUN12 FASE3 G6 (research 2026 MDN cascade layers
                  https://developer.mozilla.org/en-US/docs/Web/CSS/@layer:
                  .trust-label sin capa (12px + max-width 16ch) vence a las
                  utilidades en capa — text-[11px]/max-w-full inertes). */}
              <span className="trust-label font-semibold tracking-wide break-words text-pretty">
                {t(stat.labelKey)}
              </span>
            </div>
          ))}
        </Reveal>
      </div>
    </section>
  );
}
